import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

const API_URL = process.env.REACT_APP_API_URL

export default function Team({ role }) {
  const [team, setTeam] = useState([])
  const [loading, setLoading] = useState(true)
  const [showInvite, setShowInvite] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [selected, setSelected] = useState(null)
  const [inviteName, setInviteName] = useState('')
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('provider')
  const [inviteSpecialties, setInviteSpecialties] = useState('')
  const [inviteBio, setInviteBio] = useState('')
  const [editName, setEditName] = useState('')
  const [editEmail, setEditEmail] = useState('')
  const [editBio, setEditBio] = useState('')
  const [editSpecialties, setEditSpecialties] = useState('')
  const [editIsProvider, setEditIsProvider] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => { fetchTeam() }, [])

  async function fetchTeam() {
    const { data } = await supabase.from('staff').select('*').order('full_name')
    if (data) setTeam(data)
    setLoading(false)
  }

  async function sendInvite() {
    if (!inviteName || !inviteEmail) return
    setSaving(true)
    const { data: staffData } = await supabase.from('staff').insert({
      full_name: inviteName, email: inviteEmail, active: true,
      is_provider: inviteRole === 'provider',
      specialties: inviteSpecialties ? inviteSpecialties.split(',').map(s => s.trim()) : [],
      bio: inviteBio || null,
    }).select().single()

    await fetch(`${API_URL}/send-staff-invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ to: inviteEmail, name: inviteName, role: inviteRole }),
    })

    setSaving(false)
    setShowInvite(false)
    setInviteName(''); setInviteEmail(''); setInviteRole('provider')
    setInviteSpecialties(''); setInviteBio('')
    fetchTeam()
    alert(`Invite sent to ${inviteEmail}`)
  }

  function openEdit(member) {
    setSelected(member)
    setEditName(member.full_name || '')
    setEditEmail(member.email || '')
    setEditBio(member.bio || '')
    setEditSpecialties(member.specialties?.join(', ') || '')
    setEditIsProvider(member.is_provider !== false)
    setShowEdit(true)
  }

  async function saveEdit() {
    setSaving(true)
    await supabase.from('staff').update({
      full_name: editName, email: editEmail, bio: editBio || null,
      specialties: editSpecialties ? editSpecialties.split(',').map(s => s.trim()) : [],
      is_provider: editIsProvider,
    }).eq('id', selected.id)
    setSaving(false)
    setShowEdit(false)
    fetchTeam()
  }

  async function deleteMember() {
    if (window.confirm(`Are you sure you want to delete ${selected.full_name}? This cannot be undone.`)) {
      await supabase.from('staff').delete().eq('id', selected.id)
      setShowEdit(false)
      fetchTeam()
    }
  }

  function getInitials(name) {
    return name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?'
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Team</h1>
          <p style={styles.subtitle}>{team.length} team members</p>
        </div>
        {(role === 'owner' || role === 'admin') && (
          <button style={styles.inviteBtn} onClick={() => setShowInvite(true)}>+ Invite</button>
        )}
      </div>

      {loading ? <div style={styles.empty}>Loading...</div> : (
        <div style={styles.teamGrid}>
          {team.map(member => (
            <div key={member.id} style={{ ...styles.memberCard, opacity: member.active ? 1 : 0.5 }}>
              <div style={styles.memberAvatar}>{getInitials(member.full_name)}</div>
              <div style={styles.memberInfo}>
                <div style={styles.memberName}>{member.full_name}</div>
                <div style={styles.memberEmail}>{member.email}</div>
                <div style={styles.memberTags}>
                  {!member.is_provider && (
                    <span style={styles.adminTag}>ADMIN</span>
                  )}
                  {member.stripe_onboarding_complete && (
                    <span style={styles.stripeTag}>✓ Stripe</span>
                  )}
                  {!member.active && (
                    <span style={styles.inactiveTag}>INACTIVE</span>
                  )}
                </div>
                {member.specialties?.length > 0 && (
                  <div style={styles.specialties}>
                    {member.specialties.map(s => (
                      <span key={s} style={styles.specialtyTag}>{s}</span>
                    ))}
                  </div>
                )}
              </div>
              {(role === 'owner' || role === 'admin') && (
                <button style={styles.editBtn} onClick={() => openEdit(member)}>Edit</button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Invite Modal */}
      {showInvite && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Invite Team Member</h2>
              <button style={styles.closeBtn} onClick={() => setShowInvite(false)}>✕</button>
            </div>
            <p style={styles.modalSubtitle}>They'll receive an email to set up their account.</p>

            <label style={styles.label}>Full Name *</label>
            <input style={styles.input} value={inviteName} onChange={e => setInviteName(e.target.value)} placeholder="e.g. Sarah Johnson" />

            <label style={styles.label}>Email *</label>
            <input style={styles.input} value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} type="email" placeholder="sarah@example.com" />

            <label style={styles.label}>Role</label>
            <div style={styles.pillRow}>
              {['provider', 'admin', 'owner'].map(r => (
                <button key={r} style={{ ...styles.pill, ...(inviteRole === r ? styles.pillActive : {}) }} onClick={() => setInviteRole(r)}>
                  {r.charAt(0).toUpperCase() + r.slice(1)}
                </button>
              ))}
            </div>

            <label style={styles.label}>Specialties</label>
            <input style={styles.input} value={inviteSpecialties} onChange={e => setInviteSpecialties(e.target.value)} placeholder="Color, Extensions, Cuts" />
            <p style={styles.hint}>Separate with commas</p>

            <label style={styles.label}>Bio</label>
            <textarea style={styles.textarea} value={inviteBio} onChange={e => setInviteBio(e.target.value)} rows={3} />

            <button style={styles.saveBtn} onClick={sendInvite} disabled={saving}>
              {saving ? 'Sending...' : 'Send Invite'}
            </button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEdit && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Edit Team Member</h2>
              <button style={styles.closeBtn} onClick={() => setShowEdit(false)}>✕</button>
            </div>

            <label style={styles.label}>Full Name</label>
            <input style={styles.input} value={editName} onChange={e => setEditName(e.target.value)} />

            <label style={styles.label}>Email</label>
            <input style={styles.input} value={editEmail} onChange={e => setEditEmail(e.target.value)} type="email" />

            <label style={styles.label}>Specialties</label>
            <input style={styles.input} value={editSpecialties} onChange={e => setEditSpecialties(e.target.value)} placeholder="Color, Extensions, Cuts" />

            <label style={styles.label}>Bio</label>
            <textarea style={styles.textarea} value={editBio} onChange={e => setEditBio(e.target.value)} rows={3} />

            <label style={styles.label}>Provider Status</label>
            <div style={styles.pillRow}>
              <button style={{ ...styles.pill, ...(editIsProvider ? styles.pillActive : {}) }} onClick={() => setEditIsProvider(true)}>Provider</button>
              <button style={{ ...styles.pill, ...(!editIsProvider ? styles.pillActive : {}) }} onClick={() => setEditIsProvider(false)}>Admin Only</button>
            </div>

            <button style={styles.saveBtn} onClick={saveEdit} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>

            <button style={styles.deleteMemberBtn} onClick={deleteMember}>
              Delete Team Member
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

const styles = {
  container: { padding: '32px', maxWidth: '1200px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: {
    fontSize: '28px', fontWeight: '600', color: '#D4AF77',
    letterSpacing: '2px', textTransform: 'uppercase', fontFamily: 'Cormorant Garamond, serif',
  },
  subtitle: { fontSize: '12px', color: '#555', letterSpacing: '1px', marginTop: '4px' },
  inviteBtn: {
    padding: '10px 20px', backgroundColor: '#D4AF77', color: '#000',
    border: 'none', borderRadius: '2px', fontSize: '11px', fontWeight: '700',
    letterSpacing: '1.5px', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif',
  },
  teamGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px' },
  memberCard: {
    display: 'flex', alignItems: 'center', gap: '14px',
    backgroundColor: '#111', border: '1px solid #1a1a1a',
    borderRadius: '2px', padding: '16px',
  },
  memberAvatar: {
    width: '44px', height: '44px', borderRadius: '50%',
    backgroundColor: '#D4AF77', display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: '15px', fontWeight: '700',
    color: '#000', flexShrink: 0,
  },
  memberInfo: { flex: 1 },
  memberName: { fontSize: '15px', fontWeight: '600', color: '#F5F0E8', marginBottom: '2px' },
  memberEmail: { fontSize: '12px', color: '#555', marginBottom: '6px' },
  memberTags: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' },
  adminTag: {
    fontSize: '9px', color: '#D4AF77', letterSpacing: '1px',
    border: '1px solid rgba(212,175,119,0.3)', padding: '2px 6px', borderRadius: '2px',
  },
  stripeTag: { fontSize: '9px', color: '#4A754A', letterSpacing: '0.5px' },
  inactiveTag: { fontSize: '9px', color: '#555', letterSpacing: '1px' },
  specialties: { display: 'flex', gap: '4px', flexWrap: 'wrap' },
  specialtyTag: {
    fontSize: '10px', color: '#D4AF77',
    backgroundColor: 'rgba(212,175,119,0.1)', padding: '2px 8px', borderRadius: '2px',
  },
  editBtn: {
    padding: '6px 14px', background: 'none', border: '1px solid #D4AF77',
    color: '#D4AF77', fontSize: '11px', fontWeight: '600', letterSpacing: '1px',
    cursor: 'pointer', borderRadius: '2px', fontFamily: 'Montserrat, sans-serif', flexShrink: 0,
  },
  overlay: {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modal: {
    backgroundColor: '#111', border: '1px solid #222', borderRadius: '2px',
    padding: '32px', width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto',
  },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
  modalTitle: { fontSize: '18px', fontWeight: '600', color: '#F5F0E8' },
  modalSubtitle: { fontSize: '12px', color: '#555', marginBottom: '20px', lineHeight: '1.6' },
  closeBtn: { background: 'none', border: 'none', color: '#555', fontSize: '18px', cursor: 'pointer', padding: '4px' },
  label: {
    display: 'block', fontSize: '10px', fontWeight: '600', color: '#555',
    letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '6px', marginTop: '16px',
    fontFamily: 'Montserrat, sans-serif',
  },
  input: {
    width: '100%', padding: '12px', backgroundColor: '#0a0a0a', border: '1px solid #222',
    borderRadius: '2px', color: '#F5F0E8', fontSize: '14px',
    fontFamily: 'Montserrat, sans-serif', outline: 'none', boxSizing: 'border-box',
  },
  textarea: {
    width: '100%', padding: '12px', backgroundColor: '#0a0a0a', border: '1px solid #222',
    borderRadius: '2px', color: '#F5F0E8', fontSize: '14px',
    fontFamily: 'Montserrat, sans-serif', outline: 'none', resize: 'vertical', boxSizing: 'border-box',
  },
  hint: { fontSize: '11px', color: '#444', marginTop: '4px' },
  pillRow: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  pill: {
    padding: '8px 16px', background: 'none', border: '1px solid #222',
    color: '#555', fontSize: '11px', fontWeight: '600', letterSpacing: '1px',
    cursor: 'pointer', borderRadius: '2px', fontFamily: 'Montserrat, sans-serif',
  },
  pillActive: { backgroundColor: '#D4AF77', borderColor: '#D4AF77', color: '#000' },
  saveBtn: {
    marginTop: '24px', width: '100%', padding: '14px', backgroundColor: '#D4AF77',
    color: '#000', border: 'none', borderRadius: '2px', fontSize: '11px', fontWeight: '700',
    letterSpacing: '2px', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif',
  },
  deleteMemberBtn: {
    marginTop: '12px', width: '100%', padding: '14px', backgroundColor: 'transparent',
    color: '#ef4444', border: '1px solid #ef4444', borderRadius: '2px', fontSize: '11px',
    fontWeight: '600', letterSpacing: '2px', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif',
  },
  empty: { color: '#444', fontSize: '14px', padding: '24px', textAlign: 'center' },
}